import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuthStore } from '../../../auth/authStore';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import {
  BookingFlowResumer,
  useBookingFlowResume,
} from '../../../booking-flow/BookingFlowResumer';
import { BookingFlowStepper } from '../../../booking-flow/BookingFlowStepper';
import { airFlowConfig, isFirstBookingReservable, type AirFlowState } from '../flowConfig';
import { airResumeAdapter } from '../resume';
import { parseLegacyDateTime } from '../paymentResume';
import type { AirBooking, OriginDestinationOption, SelectableFare } from '../../../api/types/air';
import { AirSearchForm } from '../components/AirSearchForm';
import { AirResultsList } from '../components/AirResultsList';
import { AirUpsellPanel } from '../components/AirUpsellPanel';
import { AirInsurancePanel } from '../components/AirInsurancePanel';
import { AirPassengerForm } from '../components/AirPassengerForm';
import { AirApprovalPassengerStep } from '../components/AirApprovalPassengerStep';
import { approvalEmployeeId, corporateMaxBudget, needsCorporateApproval } from '../corporateApproval';
import { PaymentForm } from '../../../components/payment/PaymentForm';
import { SendForApprovalPanel } from '../../../components/booking/SendForApprovalPanel';
import { useSentForApproval } from '../../../components/booking/useSentForApproval';
import { AirSeatSelectionStep } from '../components/AirSeatSelectionStep';

/**
 * Allocate adımı — sonuç listesi + (tek yön / uluslararası gidiş-dönüş yolunda) bir uçuş
 * seçildikten sonra branded-fare Upsell ara paneli (`AirUpsellPanel`). Split (iç hat
 * gidiş-dönüş) ve çok noktalı yollar doğrudan Allocate'e gider — upsell yok. Panel
 * component ağacının dışına state taşımamak için burada, `allocate` adımına yerel.
 */
function AirAllocateStep({
  state,
  running,
  onRun,
}: {
  state: AirFlowState;
  running: boolean;
  onRun: (partial: Partial<AirFlowState>) => void;
}) {
  const [pending, setPending] = useState<{
    option: OriginDestinationOption;
    fare: SelectableFare;
  } | null>(null);

  if (pending) {
    return (
      <AirUpsellPanel
        option={pending.option}
        fare={pending.fare}
        loading={running}
        onBack={() => setPending(null)}
        onContinue={() => onRun({ selectedOption: pending.option, selectedFare: pending.fare })}
        onSelectUpsell={(option, fare) => onRun({ selectedOption: option, selectedFare: fare })}
      />
    );
  }

  return (
    <AirResultsList
      result={state.searchResult}
      loading={running}
      onSelect={(option, fare) => setPending({ option, fare })}
      onSelectRoundTrip={(departure, ret) =>
        onRun({
          selectedOption: departure.option,
          selectedFare: departure.fare,
          selectedReturnOption: ret.option,
          selectedReturnFare: ret.fare,
        })
      }
      onSelectMultiCity={(selections) => onRun({ multiCitySelections: selections })}
    />
  );
}

const EXPIRED_REDIRECT_DELAY_S = 10;

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Sekme başlığını ve sayfa üstündeki geri sayım rozetini verilen mutlak `deadlineMs`
 *  zamanına göre yönetir; süre dolunca "süreniz bitmiştir" modalını açıp 10 saniye sonra
 *  ana sayfaya yönlendirir. Deadline ya yeni bir aramanın 15 dakikası (`AirFlowState.
 *  searchDeadline`) ya da geri dönülen bir rezervasyonun gerçek `expireAt`'idir. */
function useDeadlineCountdown(deadlineMs: number | null, paymentDone: boolean) {
  const navigate = useNavigate();
  const originalTitleRef = useRef(document.title);
  const [remainingMs, setRemainingMs] = useState(0);
  const [expired, setExpired] = useState(false);
  const [redirectIn, setRedirectIn] = useState(EXPIRED_REDIRECT_DELAY_S);

  useEffect(() => {
    setExpired(false);
  }, [deadlineMs]);

  useEffect(() => {
    if (deadlineMs === null || paymentDone) return undefined;
    const tick = () => {
      const remaining = deadlineMs - Date.now();
      setRemainingMs(Math.max(0, remaining));
      if (remaining <= 0) setExpired(true);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [deadlineMs, paymentDone]);

  useEffect(() => {
    if (deadlineMs === null || paymentDone) {
      document.title = originalTitleRef.current;
      return;
    }
    document.title = expired ? 'Süreniz Doldu' : `${formatCountdown(remainingMs)} — Uçak Rezervasyonu`;
  }, [remainingMs, deadlineMs, paymentDone, expired]);

  useEffect(() => {
    if (!expired) {
      setRedirectIn(EXPIRED_REDIRECT_DELAY_S);
      return undefined;
    }
    if (redirectIn <= 0) {
      navigate('/');
      return undefined;
    }
    const timeout = setTimeout(() => setRedirectIn((s) => s - 1), 1000);
    return () => clearTimeout(timeout);
  }, [expired, redirectIn, navigate]);

  useEffect(() => () => {
    document.title = originalTitleRef.current;
  }, []);

  return { remainingMs, expired, redirectIn, active: deadlineMs !== null && !paymentDone };
}

function AirFlowInner() {
  const flow = useBookingFlowContext<AirFlowState>();
  const { resumed, deadlineMs: resumeDeadlineMs } = useBookingFlowResume();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useAuthStore((s) => s.currentUser);
  const sessionEmployeeId = useAuthStore((s) => s.employeeId);
  const navigate = useNavigate();
  const running = flow.status === 'running';

  // "Onaya Gönder": yolcu adımı (UpdatePassengers → Billing/Submit → MakePrebooking)
  // tamamlanıp bir sonraki adıma geçti → uçuş yöneticinin onayında, akıştan çık.
  useSentForApproval({
    sent: !!flow.state.sentForApproval,
    idle: flow.status === 'idle',
    currentStepId: flow.currentStep?.id,
    passengerStepId: 'passengers',
    reservationPath: '/air/reservations',
    reset: flow.reset,
    message:
      'Uçuş onaya gönderilmiştir. Yöneticiniz onayladığında rezervasyon listenizden ödemeye devam edebilirsiniz.',
  });

  // Non-3DS ödeme + biletleme (+ istenmişse sigorta zinciri) bittiğinde akış içi belge
  // ekranı yerine kesilen biletin/rezervasyonun detay sayfasına düş — legacy
  // `MakeReservedPayment` de `/flight/details`e yönlendiriyordu. (3DS yolu `/payment/return`
  // üzerinden aynı hedefe iner.)
  const flowComplete =
    flow.isLastStep &&
    flow.status === 'idle' &&
    (!!flow.state.paymentResult || !!flow.state.shoppingCart?.isFinalized);

  useEffect(() => {
    if (!flowComplete) return;
    if (flow.state.insuranceError) {
      enqueueSnackbar(`Sigorta poliçesi oluşturulamadı: ${flow.state.insuranceError}`, {
        variant: 'warning',
      });
    }
    // Detay sayfası PNR (`bookingCode`) ya da bilet no ile `Entry/Find` çeker; ikisi de
    // yoksa listeye düş. Çok noktalıda ilk bacağın PNR'ı yeterli (detay tüm bacakları birleştirir).
    const firstBooking = (flow.state.shoppingCart?.airBookings as AirBooking[] | undefined)?.[0];
    const pnr = firstBooking?.bookingCode;
    const ticket = firstBooking?.passengerBreakdowns?.find((pb) => pb.ticketNumber)?.ticketNumber;
    const target = pnr
      ? `/air/reservations/${encodeURIComponent(pnr)}?by=pnr`
      : ticket
        ? `/air/reservations/${encodeURIComponent(ticket)}?by=ticket`
        : '/air/reservations';
    flow.reset(); // taslağı temizle — sonraki /air ziyareti sıfırdan başlasın
    navigate(target, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowComplete]);

  const deadlineMs = resumeDeadlineMs ?? flow.state.searchDeadline ?? null;
  const { remainingMs, expired, redirectIn, active } = useDeadlineCountdown(
    deadlineMs,
    !!flow.state.paymentResult
  );

  const handleStepError = (action: () => Promise<unknown>) => {
    action().catch((err) => {
      enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' });
    });
  };

  return (
    <Box>
      <Dialog open={expired}>
        <DialogTitle>İşlem Süreniz Bitmiştir</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {resumed
              ? 'Bu rezervasyonun son geçerlilik süresi doldu.'
              : 'Arama sonrası tanınan 15 dakikalık süre doldu.'}{' '}
            {redirectIn} saniye içinde ana sayfaya yönlendirileceksiniz.
          </DialogContentText>
        </DialogContent>
      </Dialog>

      {active && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Chip
            size="small"
            color={remainingMs <= 60_000 ? 'error' : 'default'}
            label={`Kalan süre: ${formatCountdown(remainingMs)}`}
          />
        </Box>
      )}

      <BookingFlowStepper />
      {flow.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {flow.error}
        </Alert>
      )}

      {flow.currentStep?.id === 'search' && (
        <AirSearchForm
          loading={running}
          onSearch={(request) => handleStepError(() => flow.runStep({ searchRequest: request }))}
        />
      )}

      {flow.currentStep?.id === 'allocate' && (
        <AirAllocateStep
          state={flow.state}
          running={running}
          onRun={(partial) => handleStepError(() => flow.runStep(partial))}
        />
      )}

      {flow.currentStep?.id === 'passengers' && (() => {
        const firstBooking = (flow.state.shoppingCart?.airBookings as AirBooking[] | undefined)?.[0];
        const isInternational =
          firstBooking?.isInternational ?? flow.state.searchResult?.isInternational ?? false;

        // Allocate sonrası sepette EmployeeID var + uçuş bütçe/politika dışı → yolcu bilgisi
        // personel kaydından salt-okunur gelir, buton "Onaya Gönder".
        const approvalEmpId = approvalEmployeeId(flow.state, sessionEmployeeId);
        if (needsCorporateApproval(flow.state) && approvalEmpId) {
          return (
            <AirApprovalPassengerStep
              employeeId={approvalEmpId}
              isInternational={isInternational}
              maxBudget={corporateMaxBudget(flow.state)}
              productLabel="uçuş"
              loading={running}
              onSubmit={(passengers, contact, billingInformation) =>
                handleStepError(() =>
                  flow.runStep({ passengers, contact, billingInformation, sentForApproval: true })
                )
              }
            />
          );
        }
        return (
          <AirPassengerForm
            paxCounts={(flow.state.searchRequest?.airSearchPaxItems ?? []).map((p) => ({
              paxType: p.paxCode ?? 'ADT',
              count: p.paxCount ?? 1,
            }))}
            loading={running}
            isInternational={isInternational}
            provider={firstBooking?.provider}
            isReservable={isFirstBookingReservable(flow.state.shoppingCart)}
            onSubmit={(passengers, contact, currentAccount, billingInformation) =>
              handleStepError(() =>
                flow.runStep({
                  passengers,
                  contact,
                  selectedCurrentAccount: currentAccount,
                  billingInformation,
                })
              )
            }
          />
        );
      })()}

      {flow.currentStep?.id === 'seatSelection' && (
        <AirSeatSelectionStep
          shoppingCart={flow.state.shoppingCart}
          loading={running}
          onContinue={() => handleStepError(() => flow.runStep())}
          onShoppingCartChange={(shoppingCart) => flow.patchState({ shoppingCart })}
        />
      )}

      {flow.currentStep?.id === 'payment' && (
        <>
          <SendForApprovalPanel product="Flight" cart={flow.state.shoppingCart} />
          <AirInsurancePanel
            cart={flow.state.shoppingCart}
            wants={!!flow.state.wantsInsurance}
            onChange={(w) => flow.patchState({ wantsInsurance: w })}
          />
          <PaymentForm
            amount={flow.state.shoppingCart?.remainingTotal}
            currency="TRY"
            loading={running}
            currentAccount={flow.state.selectedCurrentAccount}
            shoppingCartId={flow.state.shoppingCart?.id}
            creditCardPaymentAllowed={currentUser?.creditCardPayment ?? true}
            threeDPaymentAllowed={!!currentUser?.threeDPayment}
            onSubmit={({ paymentForm, paymentType }) =>
              handleStepError(() => flow.runStep({ paymentForm, paymentType }))
            }
          />
        </>
      )}

      {flow.currentStep?.id === 'finalize' && !flow.state.shoppingCart?.isFinalized && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {flow.state.paymentResult ? (
            <Alert severity="warning">
              Ödemeniz alındı ancak biletleme (PNR) tamamlanamadı. Tekrar denemek için Kesinleştir'e basın.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Rezervasyonu kesinleştirmek (PNR almak) için devam edin.
            </Typography>
          )}
          <Box>
            <Button variant="contained" disabled={running} onClick={() => handleStepError(() => flow.runStep())}>
              {running ? 'İşleniyor…' : 'Kesinleştir'}
            </Button>
          </Box>
        </Box>
      )}

      {flowComplete && (
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Rezervasyonunuz tamamlandı, bilet detay sayfanıza yönlendiriliyorsunuz…
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * `?resume=<shoppingCartId>&expireAt=<DD.MM.YYYY HH:mm:ss>` — rezervasyon listesi/detay
 * sayfasındaki "Ödemeye Git" bağlantısının hedefi (bkz. modules/air/paymentResume.ts).
 * Bu, `<BookingFlowResumer>`'a `urlResume` olarak geçer ve sessionStorage taslağının
 * önüne geçer; resumer sepeti `GetShoppingCart` ile tazeleyip flow'u ödeme adımında
 * başlatır. URL param yoksa resumer varsa sessionStorage taslağından devam eder, yoksa
 * sıfırdan başlar.
 */
export default function AirFlowPage() {
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get('resume');
  const resumeExpireAt = searchParams.get('expireAt');
  const urlResume = resumeId
    ? { cartId: resumeId, deadlineMs: parseLegacyDateTime(resumeExpireAt) }
    : undefined;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Uçak Rezervasyonu
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Arama (tek yön / gidiş-dönüş / çok noktalı) → Uçuş Seçimi (Allocate) → Yolcu Bilgisi (uygunsa Ön
        Rezervasyon dahil) → Koltuk Seçimi (THYNDC) → Ödeme → Kesinleştirme akışı.
      </Typography>
      <BookingFlowResumer config={airFlowConfig} adapter={airResumeAdapter} urlResume={urlResume}>
        <AirFlowInner />
      </BookingFlowResumer>
    </Box>
  );
}
